import { useState } from "react";
import MissingProfileFieldsList from "~/components/hosts/MissingProfileFieldsList";
import EditProfileModal from "~/components/profile-components/EditProfileModal";
import { MissingUserData } from "~/utils/user-helpers";
import { Button } from "../themed/Button";
import ThemedLink from "../themed/ThemedLink";
import Modal from "../themed/Modal";
import { ProfileEditRequiredData } from "~/components/rsvp/validators";
import { If } from "../Conditional";
import { cn } from "~/utils/fontsConfig";
import { slugifyUser } from "~/utils/string";
import { ApplyToHostStepsPipelineBuilderData } from "./v2/actions/ApplyToHostStepsPipelineBuilder";
import { CustomizableLayout } from "~/types/CustomizableLayout";

type ApplyToHostImproveProfileModalProps = {
    setOpen: (show: boolean) => void;
    open: boolean;
    missingData: MissingUserData[];
    requiredData?: ProfileEditRequiredData | null;
    refetchUser: () => void;
    title?: string;
    user?: ApplyToHostStepsPipelineBuilderData["data"]["user"];
    customization?: CustomizableLayout;
};

const ApplyToHostImproveProfileModal = ({
    setOpen,
    open,
    requiredData,
    missingData,
    refetchUser: refetchUser,
    user,
    customization,
}: ApplyToHostImproveProfileModalProps) => {
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);

    return (
        <>
            <If condition={showEditProfileModal}>
                <EditProfileModal
                    isVisible={showEditProfileModal}
                    requiredData={requiredData}
                    setVisible={(e) => {
                        refetchUser();
                        setShowEditProfileModal(e);
                    }}
                    customization={customization}
                />
            </If>
            <Modal
                open={open}
                onOpenChange={setOpen}
                title="Apply to Host"
                customization={customization}
                className={cn(
                    showEditProfileModal && "opacity-0",
                    customization?.mainCardClassName,
                )}
                footer={
                    <div className="flex flex-row items-center justify-end gap-4">
                        <Button
                            className={cn(
                                "w-full max-w-[200px]",
                                customization?.buttons?.default,
                            )}
                            onClick={() => {
                                setShowEditProfileModal(true);
                            }}
                        >
                            Edit Profile
                        </Button>
                        <Button
                            variant="secondary"
                            className={cn(
                                "w-full max-w-[200px]",
                                customization?.buttons?.destructive,
                            )}
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Nevermind!
                        </Button>
                    </div>
                }
                description="We'd love to know more about you before we process your host application. Please fill out the following profile details:"
            >
                <div className="flex flex-col justify-start text-start">
                    <div className="flex flex-col justify-start text-start">
                        <div className="mt-4 text-left text-[12px] font-normal leading-normal text-white">
                            <MissingProfileFieldsList
                                missingData={missingData}
                                customization={customization}
                            />
                        </div>
                    </div>
                    <div
                        className={cn(
                            "mt-4 text-left text-[12px] font-normal leading-normal",
                            customization?.descriptionClassName,
                        )}
                    >
                        We choose hosts based on your{"  "}
                        <ThemedLink
                            href={`/profile/${slugifyUser({
                                name: user?.name,
                                id: user?.id,
                                slug: user?.slug,
                            })}`}
                            variant="tertiary"
                            className={customization?.linkClassName}
                        >
                            River Profile
                        </ThemedLink>{" "}
                        and your past engagement in the community.
                    </div>

                    <p className="mt-4">
                        <ThemedLink
                            href="https://www.getriver.io/articles/the-art-of-hosting"
                            variant="default"
                            className={customization?.linkClassName}
                        >
                            See our full hosting guide
                        </ThemedLink>
                    </p>
                </div>
            </Modal>
        </>
    );
};

export default ApplyToHostImproveProfileModal;
